<template>
  <div class="unscheduled-treatment-pg">
    <!-- Follow-ups redesign (docs/plans/2026-10-02-followups-redesign-plan.md, Phase 4). Super
         Admins get the new branch unless they switch; offices follow STAGE.brokenAppointments (src/utils/followups/uiVersion.js);
         the v-else branch below is the classic page, unchanged. The page still owns its data, its
         filters and its four period metrics — the new branch only binds them to the shared
         components. -->
    <div v-if="fuUseNewUi" class="fu-ba" data-test="fu-broken-appointments">
      <followups-ui-toggle target="old" page-key="brokenAppointments" @toggle="fuSetUi" />
      <followups-page-shell
        title="Broken appointments"
        lede="Cancellations and no-shows for patients with nothing booked in the next 30 days."
      >
        <template #tabs>
          <followups-tabs :tabs="fuTabs" active="brokenAppointments" />
        </template>

        <ErrorDisplay/>
        <followups-filter-cards
          :cards="fuPeriodCards"
          :active="fuPeriod"
          label="Broken appointments by period"
          @select="fuPeriod = $event"
        />
        <div v-if="fuPeriod" class="fu-ba__details" data-test="fu-broken-details">
          <div class="fu-ba__details-head">
            <span class="fu-ba__details-title">{{ fuPeriodTitle }}</span>
            <span class="fu-ba__details-note" data-test="fu-broken-details-note">{{ fuPeriodNote }}</span>
          </div>
          <div class="fu-ba__details-scroll">
            <table class="fu-ba__details-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Date</th>
                  <th>Description</th>
                  <th class="fu-ba__num">Loss</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(appt, index) in fuPeriodDetails" :key="`fu-ba-${fuPeriod}-${index}`" data-test="fu-broken-detail">
                  <td>{{ formatName(appt.Patient) }}</td>
                  <td class="fu-ba__nowrap">{{ appt.Date | dateformat(dateFormatStr) }}</td>
                  <td>{{ appt.ProcDescript }}</td>
                  <td class="fu-ba__num fu-ba__loss">{{ appt.AppointmentValue | money(fuCurrency) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <followups-queue
          queue="brokenAppointments"
          mode="page"
          :rows="fuRows"
          :busy="busy"
          :external-filter="!!fuPeriod"
          @counts="fuCounts = $event"
          @clear-filters="fuPeriod = null"
        >
          <template #filters>
            <followups-filter-button
              icon="cal"
              :label="fuDateLabel"
              data-test="fu-filter-date"
              :popover="fuDatePopover"
              @apply="fuApplyDates"
            />
            <followups-switch
              :value="viewByAdjustment === 'yes'"
              label="Adjustments only"
              data-test="fu-adjustments-only"
              @input="fuSetAdjustments"
            />
          </template>
        </followups-queue>
      </followups-page-shell>
    </div>
    <template v-else>
    <followups-ui-toggle target="new" page-key="brokenAppointments" @toggle="fuSetUi" />
    <h2 class="page-title mb-4">
      Broken Appointments
    </h2>
    <BrokenAppointmentsSkeleton v-if="busy" />
    <!--<b-row>
        <b-col lg="3" md="6" xs="12">
          <b-card class="border-0 unscheduled-treatment-card-wrap">
              </b-card>
        </b-col>
        <b-col lg="6" md="12" xs="12">
         <b-card class="border-0 unscheduled-treatment-card-wrap">
         </b-card>           
        </b-col>
        <b-col lg="3" md="6" xs="12">
          <b-card class="border-0 unscheduled-treatment-card-wrap">
          </b-card>
        </b-col>
    </b-row>
    -->
    <b-row v-if="allSettings" v-show="!busy">
      <!-- Modern Filter Panel -->
      <b-col xs="12" xl="4" class="px-xl-3 mb-3 mb-xl-0">
        <div class="modern-filter-panel ba-filter-panel">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-filter section-icon"></i>
              Filter Appointments
            </h5>
          </div>
          <div class="filter-content">
            <div class="filter-group">
              <label class="filter-label">From</label>
              <DatePicker
                lang="en"
                :placeholder="datepickerText"
                input-class="form-control filter-date-input"
                v-model="dateFrom"
                :format="dateFormatStr"
              >
                <i class="fa fa-calendar" slot="calendar-icon" />
              </DatePicker>
            </div>

            <div class="filter-group">
              <label class="filter-label">To</label>
              <DatePicker
                lang="en"
                :placeholder="datepickerText"
                input-class="form-control filter-date-input"
                v-model="dateTo"
                :format="dateFormatStr"
              >
                <i class="fa fa-calendar" slot="calendar-icon" />
              </DatePicker>
            </div>

            <div class="filter-group">
              <label class="filter-checkbox">
                <input type="checkbox" v-model="viewByAdjustment" true-value="yes" false-value="no" />
                <span class="checkbox-label">View by adjustments only</span>
              </label>
            </div>

            <button class="apply-filters-btn" @click="applyFilter">
              <i class="fa fa-check"></i>
              Apply Filters
            </button>
          </div>
        </div>
      </b-col>
      <!-- Modern Summary Stats Panel -->
      <b-col xs="12" xl="8" class="px-xl-3">
        <div class="ba-stats-panel">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-bar-chart section-icon"></i>
              Broken Appointments Overview
            </h5>
          </div>

          <div class="metrics-grid">
            <!-- This Week Card -->
            <div class="metric-card" :class="{ 'has-issues': brokenAppointmentsThisWeek > 0 }">
              <div class="metric-icon" :class="brokenAppointmentsThisWeek > 0 ? 'warning' : 'success'">
                <i class="fa fa-calendar-o"></i>
              </div>
              <div class="metric-content">
                <div class="metric-label">This Week</div>
                <div class="metric-value" :class="brokenAppointmentsThisWeek > 0 ? 'text-danger' : 'text-success'">
                  {{ brokenAppointmentsThisWeek }}
                </div>
                <div class="metric-subtitle" v-if="brokenAppointmentsThisWeek > 0">
                  <span class="loss-amount">{{ brokenAppointmentsTotalValueThisWeek | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span> Net Production Loss
                </div>
                <div class="metric-subtitle text-muted" v-else>No broken appointments</div>
              </div>
              <button
                v-if="brokenAppointmentsThisWeek > 0"
                class="view-details-btn"
                id="batw"
              >
                <i class="fa fa-eye"></i>
              </button>
              <b-popover target="batw" triggers="focus" placement="bottom" v-if="brokenAppointmentsThisWeek > 0">
                <template v-slot:title><b>This Week's Broken Appointments</b></template>
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(appt, index) in brokenAppointmentsDetailsThisWeek" :key="`batw-${ appt.TeamioPatientID}-${ index }`">
                      <td>{{formatName(appt.Patient)}}</td>
                      <td>{{appt.Date | dateformat(dateFormatStr)}}</td>
                      <td>{{appt.ProcDescript}}</td>
                      <td class="text-danger">{{appt.AppointmentValue | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                    </tr>
                  </tbody>
                </table>
              </b-popover>
            </div>

            <!-- Last Week Card -->
            <div class="metric-card" :class="{ 'has-issues': brokenAppointmentsLastWeek > 0 }">
              <div class="metric-icon" :class="brokenAppointmentsLastWeek > 0 ? 'warning' : 'success'">
                <i class="fa fa-calendar"></i>
              </div>
              <div class="metric-content">
                <div class="metric-label">Last Week</div>
                <div class="metric-value" :class="brokenAppointmentsLastWeek > 0 ? 'text-danger' : 'text-success'">
                  {{ brokenAppointmentsLastWeek }}
                </div>
                <div class="metric-subtitle" v-if="brokenAppointmentsLastWeek > 0">
                  <span class="loss-amount">{{ brokenAppointmentsTotalValueLastWeek | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span> Net Production Loss
                </div>
                <div class="metric-subtitle text-muted" v-else>No broken appointments</div>
              </div>
              <button
                v-if="brokenAppointmentsLastWeek > 0"
                class="view-details-btn"
                id="balw"
              >
                <i class="fa fa-eye"></i>
              </button>
              <b-popover target="balw" triggers="focus" placement="bottom" v-if="brokenAppointmentsLastWeek > 0">
                <template v-slot:title><b>Last Week's Broken Appointments</b></template>
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(appt, index) in brokenAppointmentsDetailsLastWeek" :key="`balw-${ appt.TeamioPatientID}-${ index }`">
                      <td>{{formatName(appt.Patient)}}</td>
                      <td>{{appt.Date | dateformat(dateFormatStr)}}</td>
                      <td>{{appt.ProcDescript}}</td>
                      <td class="text-danger">{{appt.AppointmentValue | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                    </tr>
                  </tbody>
                </table>
              </b-popover>
            </div>

            <!-- This Month Card -->
            <div class="metric-card" :class="{ 'has-issues': brokenAppointmentsThisMonth > 0 }">
              <div class="metric-icon" :class="brokenAppointmentsThisMonth > 0 ? 'warning' : 'success'">
                <i class="fa fa-calendar-check-o"></i>
              </div>
              <div class="metric-content">
                <div class="metric-label">This Month</div>
                <div class="metric-value" :class="brokenAppointmentsThisMonth > 0 ? 'text-danger' : 'text-success'">
                  {{ brokenAppointmentsThisMonth }}
                </div>
                <div class="metric-subtitle" v-if="brokenAppointmentsThisMonth > 0">
                  <span class="loss-amount">{{ brokenAppointmentsTotalValueThisMonth | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span> Net Production Loss
                </div>
                <div class="metric-subtitle text-muted" v-else>No broken appointments</div>
              </div>
              <button
                v-if="brokenAppointmentsThisMonth > 0"
                class="view-details-btn"
                id="batm"
              >
                <i class="fa fa-eye"></i>
              </button>
              <b-popover target="batm" triggers="focus" placement="bottom" v-if="brokenAppointmentsThisMonth > 0">
                <template v-slot:title><b>This Month's Broken Appointments</b></template>
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(appt, index) in brokenAppointmentsDetailsThisMonth" :key="`batm-${ appt.TeamioPatientID}-${ index }`">
                      <td>{{formatName(appt.Patient)}}</td>
                      <td>{{appt.Date | dateformat(dateFormatStr)}}</td>
                      <td>{{appt.ProcDescript}}</td>
                      <td class="text-danger">{{appt.AppointmentValue | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                    </tr>
                  </tbody>
                </table>
              </b-popover>
            </div>

            <!-- Last Month Card -->
            <div class="metric-card" :class="{ 'has-issues': brokenAppointmentsLastMonth > 0 }">
              <div class="metric-icon" :class="brokenAppointmentsLastMonth > 0 ? 'warning' : 'success'">
                <i class="fa fa-calendar-minus-o"></i>
              </div>
              <div class="metric-content">
                <div class="metric-label">Last Month</div>
                <div class="metric-value" :class="brokenAppointmentsLastMonth > 0 ? 'text-danger' : 'text-success'">
                  {{ brokenAppointmentsLastMonth }}
                </div>
                <div class="metric-subtitle" v-if="brokenAppointmentsLastMonth > 0">
                  <span class="loss-amount">{{ brokenAppointmentsTotalValueLastMonth | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span> Net Production Loss
                </div>
                <div class="metric-subtitle text-muted" v-else>No broken appointments</div>
              </div>
              <button
                v-if="brokenAppointmentsLastMonth > 0"
                class="view-details-btn"
                id="balm"
              >
                <i class="fa fa-eye"></i>
              </button>
              <b-popover target="balm" triggers="focus" placement="bottom" v-if="brokenAppointmentsLastMonth > 0">
                <template v-slot:title><b>Last Month's Broken Appointments</b></template>
                <table class="table table-sm">
                  <thead>
                    <tr>
                      <th>Patient</th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(appt, index) in brokenAppointmentsDetailsLastMonth" :key="`balm-${ appt.TeamioPatientID}-${ index }`">
                      <td>{{formatName(appt.Patient)}}</td>
                      <td>{{appt.Date | dateformat(dateFormatStr)}}</td>
                      <td>{{appt.ProcDescript}}</td>
                      <td class="text-danger">{{appt.AppointmentValue | money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue)}}</td>
                    </tr>
                  </tbody>
                </table>
              </b-popover>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-show="!busy" class="mt-3">
      <b-col class="px-xl-3">
        <div class="ba-table-panel">
          <div class="panel-header">
            <h5 class="section-title">
              <i class="fa fa-list section-icon"></i>
              Broken Appointments List
            </h5>
            <p class="panel-subtitle">By default these are all Broken Appointments in the last six months who have NO appointments in the next 30 days.</p>
          </div>
          <div class="table-content">
            <ErrorDisplay/>
            <AdminTable v-if="getBrokenAppointmentsData" :data="getBrokenAppointmentsData" :columns="columns" :options="options" :extra="teamioTableExtraParams" />
          </div>
        </div>
      </b-col>
    </b-row>
    </template>
    <!-- The new branch's ONE slide-out outlet (the follow-up drawer). The legacy branch's outlet
         lives inside AdminTable; v-if / v-else keeps the two from ever being mounted together. -->
    <slideout-panel v-if="fuUseNewUi"></slideout-panel>
  </div>
  <!-- unscheduled-treatment-pg -->
</template>

<script>
/* eslint-disable */

import Vue from "vue";
import DatePicker from "vue2-datepicker";
import Multiselect from "vue-multiselect";
import Widget from "@/components/Widget/Widget";
import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
import $ from "jquery";
import CommonQueries from '../../sql/queries';
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import mixin from '../../mixin.js';
import AdminTable from '@/pages/Admin/AdminTable';
import BrokenAppointmentsSkeleton from './BrokenAppointmentsSkeleton.vue';
// Follow-ups redesign (Phase 4) — the new branch of the template. See fuUseNewUi.
import FollowupsQueue from '@/components/Followups/FollowupsQueue.vue';
import FollowupsPageShell from '@/components/Followups/FollowupsPageShell.vue';
import FollowupsTabs from '@/components/Followups/FollowupsTabs.vue';
import FollowupsFilterCards from '@/components/Followups/FollowupsFilterCards.vue';
import FollowupsFilterButton from '@/components/Followups/FollowupsFilterButton.vue';
import FollowupsSwitch from '@/components/Followups/FollowupsSwitch.vue';
import FollowupsUiToggle from '@/components/Followups/FollowupsUiToggle.vue';
import { shouldUseNewFollowupsUi, browserStorage } from '@/utils/followups/uiVersion.js';
import { switchUi } from '@/utils/followups/uiSwitch.js';
import { visibleTabs } from '@/utils/followups/pageChrome.js';
import { periodCards, rowsForPatients, BROKEN_PERIODS } from '@/utils/followups/filterCards.js';
import { formatMoney } from '@/utils/followups/rowModel.js';


export default {
  name: 'BrokenAppointments',
  components: {
    DatePicker, Multiselect, Widget, ErrorDisplay, AdminTable, BrokenAppointmentsSkeleton,
    FollowupsQueue, FollowupsPageShell, FollowupsTabs, FollowupsFilterCards, FollowupsFilterButton, FollowupsSwitch, FollowupsUiToggle,
  },
  mixins: [mixin],

  created() {
    this.init();
  },

  mounted() {
    this.initCharts();
  },

  data() {
    return {
      // Follow-ups redesign (new branch only) — UI state; the data, filters and metrics below stay the truth.
      fuUiVersion: 0,      // bumped by the Classic / New view links so fuUseNewUi re-reads storage
      fuCounts: null,      // the queue's chip counts (the tab badge)
      fuPeriod: null,      // the period card that is filtering: 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth'
      results: [],
      busy: false,
      teamioTableExtraParams: {
        detailPageName: 'BrokenAppointmentDetails',
      },
      _settings: null,
      brokenAppointmentStatusColors: {
        'Archived': 'grey',
        'Call Back, Not Ready': 'primary',
        'Couldn\'t Leave Voicemail': 'pink',
        'Left Msg on Ans. Mach' : 'f87979',
        'No Answer': 'purple',
        'Not Called': 'orange',
        'Patient Asked Not To Be Called': 'danger',
        'Scheduled': 'success',
        'Patient Will Call Us': 'FF6384',
      },
      //dateFrom: this.dateFrom ? moment(this.dateFrom).format('YYYY-MM-DD') :  moment().subtract(3, 'months').format('YYYY-MM-DD'),
      dateFrom: '',
      //dateTo: moment().format('YYYY-MM-DD'),
      dateTo: '',
      viewByAdjustment: 'no',
      datepickerText: "",
      amountRemainingOver: 0,
      color: {
        hex: "#cccccc"
      },
      isPickerActive: false,
      selectedCodes: [],
      selectedPlans: [],
      selectedProvider: '',
      selectedList : 'Select',
      selectedListName: "Select",
      listTitle: 'My Sample List',
      listAssignedTo: [],
      selectedList: [],
      columns: ['Patient', 'dateBroken', 'AdjustmentProcedureCode', 'fee', 'Status','FollowUpActionPlan', 'Attempts'],
      options: {
          skin:'table',
          sortable: ['Patient', 'dateBroken', 'AdjustmentProcedureCode', 'fee', 'Status', 'Attempts'],
          perPage: 50,
          sortByColumn: true,
          pagination: { chunk: 10, dropdown: false },
          filterByColumn: true,               // https://matanya.gitbook.io/vue-tables-2/list-filters
          filterable: ['Patient', 'AdjustmentProcedureCode', 'fee', 'Status', 'Attempts'],
          headings: {
            Patient: 'Patient',
            dateBroken: 'Date',
            AdjustmentProcedureCode: 'Procedure Code',
            fee: 'Fee',
            Status: 'Status',
            FollowUpActionPlan: 'Follow Up Action Plan',
            Attempts: 'Attempts'
          },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          },
          sortIcon: {
            base: "fa",
            is: "fa-sort",
            up: "fa-sort-asc",  
            down: "fa-sort-desc"
          },
          customSorting: {
              fee: function (ascending) {
                return function (a, b) {
                  //console.log(a, b);
                  if(ascending)
                    return a.Fee - b.Fee;
                  else
                    return b.Fee - a.Fee;
                }
              },
          }, 
          texts: {
            filter: 'Filter by Patient'
          },
          initFilters: {
            //'AdjustmentProcedureCode': 'Filter by Proc Code'
          }
      },
      // isMainChecked: false,
        patientColumns:['TeamioPatientID','firstName', 'lastName'],
        patientOptions: {
          skin:'table',
          sortable: [],
          perPage: 50,
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          columnsClasses: {PatNum:'text-center'},
          filterable: false
        },
    };
  },
  methods: {
    ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
    ...mapActions('teamio', ['runQuery']),
    ...mapActions('lists',['createList']),
    ...mapMutations('teamio', ['emptyResults']),
    ...mapMutations('lists', ['setLists', 'setMasterList']),

    // ---- Follow-ups redesign (new branch only). Each one hands off to the page's EXISTING models
    // ---- and methods (dateFrom / dateTo / viewByAdjustment → applyFilter → init); none
    // ---- re-implements them.

    /** The "Classic view" / "New view" links: a Super Admin re-renders in place, an office user reloads (uiSwitch.js). */
    fuSetUi(target) {
      if (switchUi('brokenAppointments', target, { isSuperAdmin: this.isSuperAdmin, userKey: this.userID }) === 'rerender') this.fuUiVersion += 1;
    },

    /** dateFrom / dateTo hold a Date (picker), 'MM/DD/YY' (init) or nothing → 'YYYY-MM-DD'. */
    fuYmd(v) {
      if (!v) return '';
      const m = typeof v === 'string' && /^\d{2}\/\d{2}\/\d{2}$/.test(v) ? moment(v, 'MM/DD/YY') : moment(v);
      return m.isValid() ? m.format('YYYY-MM-DD') : '';
    },

    /** The date popover → the old panel's From / To, then its Apply Filters button. */
    fuApplyDates({ from, to }) {
      this.dateFrom = moment(from, 'YYYY-MM-DD').toDate();
      this.dateTo = moment(to, 'YYYY-MM-DD').toDate();
      this.applyFilter();
    },

    /**
     * "Adjustments only" → the old "View by adjustments only" checkbox ('yes' / 'no'). The old page
     * waited for Apply Filters; a switch applies at once, through the same applyFilter().
     */
    fuSetAdjustments(on) {
      this.viewByAdjustment = on ? 'yes' : 'no';
      this.applyFilter();
    },

    async init() {
      this.busy = true;
      //let dateFrom = this.dateFrom ? moment(this.dateFrom).format('YYYY-MM-DD') :  moment().subtract(3, 'months').format('YYYY-MM-DD');
      //let dateTo = this.dateTo ? moment(this.dateTo).format('YYYY-MM-DD') :  moment().format('YYYY-MM-DD');

      let dateFrom = this.dateFrom ? moment(this.dateFrom).format('MM/DD/YY') :  moment().subtract(3, 'months').format('MM/DD/YY');
      let dateTo = this.dateTo ? moment(this.dateTo).format('MM/DD/YY') :  moment().format('MM/DD/YY');
      this.dateFrom = dateFrom;
      this.dateTo = dateTo;

      this.resetError();

      let query = {
          type: 'BROKEN_APPOINTMENTS',
          siteID: this.siteID,
          dateFrom: moment(dateFrom).format('YYYY-MM-DD'),  // have to do this here coz backedn expects it in this format..don't change this
          dateTo: moment(dateTo).format('YYYY-MM-DD'), // have to do this here coz backedn expects it in this format..don't change this
          viewByAdjustment: this.viewByAdjustment,
          /*
          adjustments: this.brokenApptAdjustments,
          codes: this.brokenApptCodes,
          */          
          adjustments: localStorage.getItem('brokenApptAdjustments') || '',
          codes: localStorage.getItem('brokenApptCodes') || '',
          _date: moment().format('YYYY-MM-DD'),
          country: localStorage.getItem('country') || 'USA'
      }

      this.emptyResults();
      try {
        this.results = await this.runQuery(query);
      } finally {
        this.busy = false;
      }
    },

    isDueToday(d) {
      if(d === null || d === '')
        return false;
      else
        return moment(d).isSame(moment(), 'day');
    },

    isEmpty(obj) {
      for(var key in obj) {
          if(obj.hasOwnProperty(key))
              return false;
      }
      return true;
    },

    applyFilter() {
      //console.log('applying filter');
      let diff = moment(this.dateTo).diff(moment(this.dateFrom), 'months', true);
      //console.log(`diff: ${diff}`);
      if(diff > 24) {
        this.$toast.error('You can only filter with maximum of 24 months difference between the dates. Please try again.');        
        return false;  
      }
      this.init();
    },

    removeCode(option) {
      let removeIdx = this.selectedCodes.findIndex(each => {
        return each.ProcCode === option.ProcCode;
      });
      this.selectedCodes.splice(removeIdx, 1);
    },

    hideModal(ref) {
      this.$refs[ref].hide();
    },

    initCharts() {
      // eslint-disable-line
      $("#easy-pie1").easyPieChart({
        barColor: "#8fe5d4",
        trackColor: "#f8f9fa",
        scaleColor: false,
        lineWidth: 10,
        size: 120
      });
    },

    chooseNone() {
      this.selectedPatients = [];
    },

    chooseAll(){
      //_.take(this.dataToDisplay[0],10).forEach(p =>this.selectedPatients.push(p));
      if(this.brokenAppointmentsData)
        this.brokenAppointmentsData.forEach(p =>this.selectedPatients.push(p));
    },

    handleMainCheckbox() {
      this.isMainChecked ? this.chooseAll() : this.chooseNone();
    },

    codeWithDesc({ ProcCode, Descript }){
      return `${ProcCode} — [${Descript}]`;
    },

    successCountDownChanged(count) {
        this.setDismissSuccessCountDown(count);
    },
    
  },

  computed: {

    // ---- Follow-ups redesign (new branch only) -------------------------------------------------

    /**
     * Which template branch renders. An office user → STAGE.brokenAppointments and their own choice (the old page at "preview"). A Super Admin → the new page unless "Classic view" was picked.
     */
    fuUseNewUi() {
      // eslint-disable-next-line no-unused-expressions
      this.fuUiVersion; // re-read storage after the Classic / New view link
      return shouldUseNewFollowupsUi('brokenAppointments', { isSuperAdmin: this.isSuperAdmin, userKey: this.userID, storage: browserStorage() });
    },

    // The tab bar: the header's permission rule; a count only for this page, whose rows are loaded.
    fuTabs() {
      const counts = {};
      if (this.getBrokenAppointmentsData && this.fuCounts) counts.brokenAppointments = this.fuCounts.now;
      return visibleTabs((a, s) => !!(this.$can && this.$can(a, s)), counts);
    },

    fuCurrency() {
      const s = this.allSettings && this.allSettings.GENERAL_CURRENCY_SYMBOL;
      return s && s.PropertyValue ? s.PropertyValue : '$';
    },

    // The four cards: the old metric cards' numbers, read from the page's own computeds.
    fuPeriodCards() {
      return periodCards({
        thisWeek: { count: this.brokenAppointmentsThisWeek, lost: this.brokenAppointmentsTotalValueThisWeek },
        lastWeek: { count: this.brokenAppointmentsLastWeek, lost: this.brokenAppointmentsTotalValueLastWeek },
        thisMonth: { count: this.brokenAppointmentsThisMonth, lost: this.brokenAppointmentsTotalValueThisMonth },
        lastMonth: { count: this.brokenAppointmentsLastMonth, lost: this.brokenAppointmentsTotalValueLastMonth },
      }, { money: n => formatMoney(n, this.fuCurrency) });
    },

    // The broken appointments behind the card that is on — the rows the old eye-icon popover listed.
    fuPeriodDetails() {
      const by = {
        thisWeek: this.brokenAppointmentsDetailsThisWeek,
        lastWeek: this.brokenAppointmentsDetailsLastWeek,
        thisMonth: this.brokenAppointmentsDetailsThisMonth,
        lastMonth: this.brokenAppointmentsDetailsLastMonth,
      };
      return (this.fuPeriod && by[this.fuPeriod]) || [];
    },

    // What the table shows: the page's list, narrowed to the patients behind the card that is on.
    fuRows() {
      const rows = this.busy ? [] : (this.getBrokenAppointmentsData || []);
      return this.fuPeriod ? rowsForPatients(rows, this.fuPeriodDetails) : rows;
    },

    fuPeriodTitle() {
      const p = BROKEN_PERIODS.find(x => x.key === this.fuPeriod);
      const n = this.fuPeriodDetails.length;
      return `${p ? p.label : ''} · ${n} broken appointment${n === 1 ? '' : 's'}`;
    },

    // The cards count every broken appointment in the period; the list below only has patients with
    // nothing booked in the next 30 days, inside the date range — say how many made it.
    fuPeriodNote() {
      const people = {};
      this.fuPeriodDetails.forEach((a) => { if (a && a.PatNum !== null && a.PatNum !== undefined) people[String(a.PatNum)] = true; });
      const total = Object.keys(people).length;
      const listed = this.fuRows.length;
      return `${listed} of ${total} patient${total === 1 ? '' : 's'} ${listed === 1 ? 'is' : 'are'} in the list below`;
    },

    fuToday() {
      return moment().format('YYYY-MM-DD');
    },

    fuDatePopover() {
      return {
        kind: 'date',
        title: 'Date broken',
        from: this.fuYmd(this.dateFrom),
        to: this.fuYmd(this.dateTo),
        today: this.fuToday,
        width: 330,
        estHeight: 300,
      };
    },

    fuDateLabel() {
      const from = this.fuYmd(this.dateFrom);
      const to = this.fuYmd(this.dateTo);
      if (!from || !to) return 'Date broken';
      const fmt = this.dateFormatStr || 'MM/DD/YYYY';
      return `Broken ${moment(from).format(fmt).slice(0, 5)} – ${moment(to).format(fmt)}`;
    },

    dataToDisplay() {
      if(this.results && this.results.data && this.results.data.length > 0)
          return this.results.data;
    },

    brokenAppointmentsData() {
      if(this.dataToDisplay)
        return this.dataToDisplay[0];
    },

    /* Looks at selectedPatients list and if any of them belongs to a list then it will complain */
    somePatientsBelongsToList() {
      if (this.selectedPatients.some(p => p.BelongsToListID !== null)) {
        return true;
      } else {
        return false;
      }
    },

    getCodeOptions() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[0];
    },

    getBrokenAppointmentsData() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[1];
    },

    brokenAppointmentsDetailsThisWeek() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0) {
        let temp = this.dataToDisplay[2].filter(a => a.MarkedBrokenOn === null ? moment(a.Date).week() === moment().week() && moment(a.Date).year() === moment().year() : moment(a.MarkedBrokenOn).week() === moment().week() && moment(a.MarkedBrokenOn).year() === moment().year());
        return temp;
      }
      
      /*return this.dataToDisplay[2].filter(a => moment(a.Date).week() === moment().week() && moment(a.Date).year() === moment().year());*/
    },

    brokenAppointmentsThisWeek() {
      if(this.brokenAppointmentsDetailsThisWeek)
        return this.brokenAppointmentsDetailsThisWeek.length;
    },

    brokenAppointmentsTotalValueThisWeek() {
      if(this.brokenAppointmentsDetailsThisWeek)
        return _.sumBy(this.brokenAppointmentsDetailsThisWeek, 'AppointmentValue');
    },

    brokenAppointmentsTotalValueLastWeek() {
      if(this.brokenAppointmentsDetailsLastWeek)
        return _.sumBy(this.brokenAppointmentsDetailsLastWeek, 'AppointmentValue');
    },

    brokenAppointmentsTotalValueThisMonth() {
      if(this.brokenAppointmentsDetailsThisMonth)
        return _.sumBy(this.brokenAppointmentsDetailsThisMonth, 'AppointmentValue');
    },

    brokenAppointmentsTotalValueLastMonth() {
      if(this.brokenAppointmentsDetailsLastMonth)
        return _.sumBy(this.brokenAppointmentsDetailsLastMonth, 'AppointmentValue');
    },

    brokenAppointmentsDetailsLastWeek() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0) {
        let temp = this.dataToDisplay[2].filter(a => a.MarkedBrokenOn === null ? moment(a.Date).week() === moment().subtract(1, 'week').week() : moment(a.MarkedBrokenOn).week() === moment().subtract(1, 'week').week());
        //return this.dataToDisplay[2].filter(a => moment(a.MarkedBrokenOn).week() === moment().subtract(1, 'week').week());
        return temp;
      }
    },

    brokenAppointmentsLastWeek() {
      if(this.brokenAppointmentsDetailsLastWeek)
        return this.brokenAppointmentsDetailsLastWeek.length;
    },

    brokenAppointmentsDetailsThisMonth() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0) {
        let temp = this.dataToDisplay[2].filter(a => a.MarkedBrokenOn === null ? moment(a.Date).month() === moment().month() : moment(a.MarkedBrokenOn).month() === moment().month());
        return temp;        
      }
    },

    brokenAppointmentsThisMonth() {
      if(this.brokenAppointmentsDetailsThisMonth)
        return this.brokenAppointmentsDetailsThisMonth.length;
    },

    brokenAppointmentsDetailsLastMonth() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0) {
        let temp;
        if(moment().month() === 0) {
          //return this.dataToDisplay[2].filter(a => moment(a.MarkedBrokenOn).month() === 11 && moment(a.MarkedBrokenOn).year() === moment().year()-1);
          temp = this.dataToDisplay[2].filter(a => a.MarkedBrokenOn === null ? moment(a.Date).month() === 11 && moment(a.Date).year() === moment().year()-1 : moment(a.MarkedBrokenOn).month() === 11 && moment(a.MarkedBrokenOn).year() === moment().year()-1);
          return temp;
        } else {
          //return this.dataToDisplay[2].filter(a => moment(a.MarkedBrokenOn).month() === moment().subtract(1, 'month').month() && moment(a.MarkedBrokenOn).year() === moment().year());
          temp = this.dataToDisplay[2].filter(a => a.MarkedBrokenOn === null ? moment(a.Date).month() === moment().subtract(1, 'month').month() && moment(a.Date).year() === moment().year() : moment(a.MarkedBrokenOn).month() === moment().subtract(1, 'month').month() && moment(a.MarkedBrokenOn).year() === moment().year());
          return temp;
        }
      }
    },

    brokenAppointmentsLastMonth() {
      if(this.brokenAppointmentsDetailsLastMonth)
        return this.brokenAppointmentsDetailsLastMonth.length;
    },

    /*
    completedAppointmentsThisWeek() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[3].filter(a => moment(a.AptDateTime).week() === moment().week() && moment(a.AptDateTime).year() === moment().year()).length;
    },
    */

    /*
    completedAppointmentsLastWeek() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[3].filter(a => moment(a.AptDateTime).week() === moment().subtract(1, 'week').week() && moment(a.AptDateTime).year() === moment().year()).length;
    },
    */

    /*
    completedAppointmentsThisMonth() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[3].filter(a => moment(a.AptDateTime).month() === moment().month() && moment(a.AptDateTime).year() === moment().year()).length;
    },
    */

    /*
    completedAppointmentsLastMonth() {
      if(this.dataToDisplay && this.dataToDisplay.length > 0)
        return this.dataToDisplay[3].filter(a => moment(a.AptDateTime).month() === moment().subtract(1, 'month').month() && moment(a.AptDateTime).year() === moment().year()).length;
    },
    */

    /*
    brokenAppointmentsAvgProdLostThisWeek() {
      if(this.completedAppointmentsThisWeek !== 0)
        return (this.brokenAppointmentsThisWeek / this.completedAppointmentsThisWeek) * 100;
      else
        return 0;
    },
    */

    /*
    brokenAppointmentsAvgProdLostLastWeek() {
      if(this.completedAppointmentsLastWeek !== 0)
        return (this.brokenAppointmentsLastWeek / this.completedAppointmentsLastWeek) * 100;
      else
        return 0;
    },
    */

    /*
    brokenAppointmentsAvgProdLostThisMonth() {
      if(this.completedAppointmentsThisMonth !== 0)
        return (this.brokenAppointmentsThisMonth / this.completedAppointmentsThisMonth) * 100;
      else
        return 0;
    },
    */

    /*
    brokenAppointmentsAvgProdLostLastMonth() {
      if(this.completedAppointmentsLastMonth !== 0)
        return (this.brokenAppointmentsLastMonth / this.completedAppointmentsLastMonth).toFixed(2) * 100;
      else
        return 0;
    },
    */

  },
};
</script>

<style src="./BrokenAppointments.scss" lang="scss" scoped />
<!-- The redesign's page-level styles (new branch only) live with the Follow-ups pages. -->
<style src="../Followups/broken-appointments-page.scss" lang="scss" scoped />
<style>
.VuePagination{
  flex-direction: row-reverse;
  float: right;
  margin: 0 !important;
  padding: 0 !important;
  }

  .VueTables__limit{
    display: none !important;
  }
  .VueTables--client > .row{
    margin: 0 !important;
  }

  .unscheduled-treatment-table .table th span {
    font-size: 10px;
    font-weight: 600;
  }

  .unscheduled-treatment-table .table th span.VueTables__heading {
    font-size: 12px;
    border-top: 1px solid #d6dee5 !important;
    font-weight: bold;
  }

  .unscheduled-treatment-table .table th {
    border-top: 1px solid #d6dee5 !important;
  }

  .unscheduled-treatment-table .table td{
    padding: 15px 12px !important;
    font-size: 12px;
    vertical-align: middle;
  }
  .patient-name{
    color: #222;
    text-decoration: none;
  }
  .patient-name:hover {
    color: #222;
    text-decoration: none;
  }
.view_BA{
  width: fit-content;
}
.view_BA_from,.view_BA_to{
  width: 210px;
}
/* Scope checkbox styles to filter section only */
.unscheduled-treatment-card-wrap .abc-checkbox label::after{
  display: inline-block;
  cursor: pointer;
  position: absolute;
  width: 16px;
  height: 16px;
  left: 0;
  top: 2px;
  margin-left: -1.25rem;
  padding-left: 3px;
  padding-top: 1px;
  font-size: 11px;
  background: #3bbe94;
}
.unscheduled-treatment-card-wrap .abc-checkbox input[type="checkbox"]:checked + label::after,
.unscheduled-treatment-card-wrap .abc-checkbox input[type="radio"]:checked + label::after{
  color: #fffefe !important;
  background-image: none !important;
}

/* =============================================
   Filter Panel variant - Broken Appointments
   Uses shared .modern-filter-panel (see src/styles/_general.scss)
   and only overrides the two cosmetic divergences.
   Compound selector (.modern-filter-panel.ba-filter-panel)
   guarantees specificity win over global .modern-filter-panel rules.
   ============================================= */

.modern-filter-panel.ba-filter-panel .filter-content {
  padding: 1.25rem;
  justify-content: center;
}

.modern-filter-panel.ba-filter-panel .apply-filters-btn {
  margin-top: 0.5rem;
}

/* =============================================
   Modern Summary Stats Panel - Broken Appointments
   ============================================= */

.ba-stats-panel {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  height: 100%;
}

.ba-stats-panel .panel-header {
  background-color: #f8fafc;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.ba-stats-panel .section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ba-stats-panel .section-icon {
  color: #3abf94;
  font-size: 1.125rem;
}

.ba-stats-panel .metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
}

.ba-stats-panel .metric-card {
  padding: 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  border-right: 1px solid #e2e8f0;
  position: relative;
  transition: background-color 0.2s ease;
}

.ba-stats-panel .metric-card:last-child {
  border-right: none;
}

.ba-stats-panel .metric-card:hover {
  background-color: #f8fafc;
}

.ba-stats-panel .metric-icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  flex-shrink: 0;
}

.ba-stats-panel .metric-icon.success {
  background-color: rgba(58, 191, 148, 0.1);
  color: #3abf94;
}

.ba-stats-panel .metric-icon.warning {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.ba-stats-panel .metric-content {
  flex: 1;
  min-width: 0;
}

.ba-stats-panel .metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.ba-stats-panel .metric-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.ba-stats-panel .metric-subtitle {
  font-size: 0.8125rem;
  color: #64748b;
  margin-top: 0.25rem;
}

.ba-stats-panel .metric-subtitle .loss-amount {
  color: #ef4444;
  font-weight: 600;
}

.ba-stats-panel .view-details-btn {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.ba-stats-panel .view-details-btn:hover {
  background: #3abf94;
  border-color: #3abf94;
  color: white;
}

/* Responsive breakpoints */
@media (max-width: 1400px) {
  .ba-stats-panel .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .ba-stats-panel .metric-card:nth-child(2) {
    border-right: none;
  }

  .ba-stats-panel .metric-card:nth-child(1),
  .ba-stats-panel .metric-card:nth-child(2) {
    border-bottom: 1px solid #e2e8f0;
  }
}

@media (max-width: 991px) {
  .ba-stats-panel .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .ba-stats-panel .metrics-grid {
    grid-template-columns: 1fr;
  }

  .ba-stats-panel .metric-card {
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }

  .ba-stats-panel .metric-card:last-child {
    border-bottom: none;
  }
}

@media (max-width: 576px) {
  .ba-stats-panel .metric-card {
    padding: 1rem;
  }

  .ba-stats-panel .metric-value {
    font-size: 1.5rem;
  }

  .ba-stats-panel .metric-icon {
    width: 2.25rem;
    height: 2.25rem;
    font-size: 1rem;
  }
}

/* Popover table styling */
.ba-stats-panel .popover {
  max-width: 500px;
}

.ba-stats-panel .popover .table-sm {
  font-size: 0.8125rem;
}

.ba-stats-panel .popover .table-sm th {
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.ba-stats-panel .popover .table-sm td {
  vertical-align: middle;
}

/* =============================================
   Modern Table Panel - Broken Appointments
   ============================================= */

.ba-table-panel {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.ba-table-panel .panel-header {
  background-color: #f8fafc;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.ba-table-panel .section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ba-table-panel .section-icon {
  color: #3abf94;
  font-size: 1rem;
}

.ba-table-panel .panel-subtitle {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0;
}

.ba-table-panel .table-content {
  padding: 0;
}

/* Override AdminTable styles within ba-table-panel */
.ba-table-panel .admin-table-wrapper {
  margin: 0;
}

.ba-table-panel .table-responsive {
  border-radius: 0;
  margin: 0 !important;
}

.ba-table-panel .VueTables {
  margin: 0;
}

.ba-table-panel .VueTables__search-field {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.ba-table-panel .VueTables__search-field input {
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.ba-table-panel .VueTables__search-field input:focus {
  outline: none;
  border-color: #3abf94;
  box-shadow: 0 0 0 2px rgba(58, 191, 148, 0.2);
}

.ba-table-panel .table {
  margin-bottom: 0;
  font-size: 0.875rem;
}

.ba-table-panel .table thead th {
  background-color: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.75rem 1rem;
}

.ba-table-panel .table tbody td {
  padding: 0.875rem 1rem;
  vertical-align: middle;
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
}

.ba-table-panel .table tbody tr:hover {
  background-color: #f8fafc;
}

.ba-table-panel .table tbody tr:last-child td {
  border-bottom: none;
}

/* Pagination styling */
.ba-table-panel .VuePagination {
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.ba-table-panel .VuePagination nav {
  display: flex;
  justify-content: flex-end;
}

.ba-table-panel .VuePagination__pagination {
  margin: 0;
}

.ba-table-panel .VuePagination__pagination li a {
  border-radius: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
}

</style>
